import type { Observable, OperatorFunction, Subscription } from 'rxjs'
import { Observable as RxObservable } from 'rxjs'

/**
 * One value's work at a time, in arrival order. A `folds` value waiting behind a run is replaced by the
 * next one rather than queued: for a pass that re-reads everything, ten queued would do one's work ten times.
 */
export const queued =
  <T, R>(project: (value: T) => Observable<R>, folds: (value: T) => boolean): OperatorFunction<T, R> =>
  (source) =>
    new RxObservable<R>((subscriber) => {
      const waiting: T[] = []
      let running = false
      let sourceDone = false
      let inner: Subscription | null = null

      const run = (value: T) => {
        running = true
        inner = project(value).subscribe({
          next: (result) => subscriber.next(result),
          error: (error: unknown) => subscriber.error(error),
          complete: () => {
            running = false
            if (waiting.length > 0) {
              run(waiting.shift() as T)
            } else if (sourceDone) {
              subscriber.complete()
            }
          }
        })
      }

      const outer = source.subscribe({
        next: (value) => {
          if (!running) {
            run(value)
            return
          }
          const folded = folds(value) ? waiting.findIndex(folds) : -1
          if (folded >= 0) {
            waiting[folded] = value
          } else {
            waiting.push(value)
          }
        },
        error: (error: unknown) => subscriber.error(error),
        complete: () => {
          sourceDone = true
          if (!running) {
            subscriber.complete()
          }
        }
      })

      return () => {
        outer.unsubscribe()
        inner?.unsubscribe()
      }
    })

/** Like `concatMap`, but every request made while a run is going folds into a single run afterwards. */
export const serially = <T, R>(project: (value: T) => Observable<R>): OperatorFunction<T, R> => queued(project, () => true)
